import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { REQUIREMENTS, submitEnquiry } from "@/lib/site";

export default function QuoteModal({ state, onClose }) {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", requirement: REQUIREMENTS[0], message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (state.open && state.requirement) {
      const match = REQUIREMENTS.find((r) => r === state.requirement);
      setForm((f) => ({
        ...f,
        requirement: match || f.requirement,
        message: match ? f.message : `Requirement: ${state.requirement}\n\n${f.message}`,
      }));
    }
  }, [state]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitEnquiry({ ...form, type: "quote" });
      toast.success("Quote request received. Our team will respond shortly.");
      setForm({ name: "", company: "", email: "", phone: "", requirement: REQUIREMENTS[0], message: "" });
      onClose();
    } catch {
      toast.error("Could not send your request. Please email info@jaytrika.in.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none transition-all duration-200 placeholder:text-slate-500 focus:border-cyan-400/50 focus:ring-2 focus:ring-cyan-400/20";

  return (
    <AnimatePresence>
      {state.open && (
        <motion.div
          data-testid="quote-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Request a quote"
        >
          <div className="absolute inset-0 bg-navy-900/80 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 28, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/10 bg-navy-800 p-7 shadow-2xl sm:p-9"
            data-lenis-prevent
          >
            <button
              data-testid="quote-close-button"
              onClick={onClose}
              aria-label="Close quote form"
              className="absolute right-5 top-5 rounded-full border border-white/10 p-2 text-slate-400 transition-colors hover:border-cyan-400/40 hover:text-white"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-3">
              <LogoMark size={34} />
              <div>
                <h3 className="font-heading text-xl font-bold text-white">Request a Quote</h3>
                <p className="text-xs text-slate-400">24/7 response · info@jaytrika.in</p>
              </div>
            </div>

            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Name *" className={inputCls} />
                <input data-testid="quote-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
                <input data-testid="quote-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
                <input data-testid="quote-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="Phone number" className={inputCls} />
              </div>
              <select data-testid="quote-requirement-select" value={form.requirement} onChange={set("requirement")} className={`${inputCls} bg-navy-800`}>
                {REQUIREMENTS.map((r) => (
                  <option key={r} value={r} className="bg-navy-800">{r}</option>
                ))}
              </select>
              <textarea data-testid="quote-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us quantities, routes, timelines or site details." className={`${inputCls} resize-none`} />
              <button
                data-testid="quote-submit-button"
                type="submit"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-electric px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? "Sending..." : "Submit Quote Request"}
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
